import type { Editor } from '@tiptap/react'
import { NodeSelection, TextSelection } from '@tiptap/pm/state'
import type { EditorState } from '@tiptap/pm/state'

/**
 * Moving, duplicating and deleting a whole BLOCK, in one place.
 *
 * Two surfaces need exactly the same answers — the gutter's own menu, and the
 * keyboard — and the reason they are written once is the reason this product
 * writes most things once: a menu that can move a block up while the keyboard
 * cannot is a product where the mouse is the real interface and the keyboard
 * is a courtesy. That is the specific trap a drag handle sets, and it is why
 * there is no drag here without a keystroke beside it.
 */

/** The top-level block the selection is inside, as a position and a depth-1 index. */
export function blockAt(state: EditorState): { pos: number; index: number; size: number } | null {
  const { $from } = state.selection
  // Depth 1 is a child of the document: the thing a person calls a block. A
  // list ITEM is deeper than that, and moving one out of its list is a
  // different operation with a different meaning.
  if ($from.depth === 0) {
    // A node selection on a top-level block — the gutter's own drag makes one.
    const at = state.selection instanceof NodeSelection ? state.selection.from : null
    if (at === null) return null
    const node = state.doc.nodeAt(at)
    return node ? { pos: at, index: state.doc.resolve(at).index(0), size: node.nodeSize } : null
  }
  const pos = $from.before(1)
  const node = state.doc.nodeAt(pos)
  if (!node) return null
  return { pos, index: state.doc.resolve(pos).index(0), size: node.nodeSize }
}

/**
 * Move the current block one place up or down.
 *
 * Cut and paste rather than a swap: a swap has to reason about two nodes of
 * different sizes and gets it wrong at the ends, and `ReplaceStep` through
 * `deleteRange` + `insert` is what ProseMirror's own history records as one
 * undoable action — which is what a person expects from one keystroke.
 */
export function moveBlock(editor: Editor, direction: -1 | 1): boolean {
  const { state } = editor
  const here = blockAt(state)
  if (!here) return false
  const parent = state.doc
  const target = here.index + direction
  if (target < 0 || target >= parent.childCount) return false

  const node = parent.child(here.index)
  const neighbour = parent.child(target)
  /*
   * Where it goes, in the coordinates of the document as it is NOW — the
   * mapping below moves it through the deletion.
   *
   * Down is past the far side of the next block, which is
   * `here.pos + this + next`; up is the near side of the previous one. The
   * first version wrote `here.pos + next.nodeSize` for down, which maps
   * straight back to where the block already was: the command ran, the
   * transaction dispatched, and the document was identical. Nothing failed,
   * which is why the gate caught it and reading the code did not.
   */
  const insertAt =
    direction === -1 ? here.pos - neighbour.nodeSize : here.pos + node.nodeSize + neighbour.nodeSize

  const tr = state.tr
  tr.delete(here.pos, here.pos + node.nodeSize)
  const mapped = tr.mapping.map(insertAt)
  tr.insert(mapped, node)
  /*
   * Put the caret back INSIDE the block that moved, so a second press moves
   * the same block again. Without this the selection lands wherever the
   * mapping left it and the second keystroke moves a different paragraph —
   * which reads as the product ignoring you.
   */
  const landed = tr.doc.resolve(Math.min(mapped + 1, tr.doc.content.size))
  tr.setSelection(TextSelection.near(landed))
  tr.scrollIntoView()
  editor.view.dispatch(tr)
  editor.view.focus()
  return true
}

/** Put a copy of the block immediately after it, and leave the caret in the copy. */
export function duplicateBlock(editor: Editor): boolean {
  const { state } = editor
  const here = blockAt(state)
  if (!here) return false
  const node = state.doc.child(here.index)
  const tr = state.tr.insert(here.pos + node.nodeSize, node.copy(node.content))
  tr.setSelection(TextSelection.near(tr.doc.resolve(here.pos + node.nodeSize + 1)))
  tr.scrollIntoView()
  editor.view.dispatch(tr)
  editor.view.focus()
  return true
}

export function deleteBlock(editor: Editor): boolean {
  const { state } = editor
  const here = blockAt(state)
  if (!here) return false
  const node = state.doc.child(here.index)
  /*
   * A document with nothing in it cannot hold a caret, so removing the last
   * block leaves an empty paragraph rather than an empty document. ProseMirror
   * would otherwise refuse the transaction against the schema and the press
   * would appear to do nothing.
   */
  const tr = state.tr.delete(here.pos, here.pos + node.nodeSize)
  if (tr.doc.childCount === 0) {
    tr.insert(0, state.schema.nodes['paragraph']!.create())
  }
  tr.setSelection(TextSelection.near(tr.doc.resolve(Math.min(here.pos, tr.doc.content.size))))
  editor.view.dispatch(tr)
  editor.view.focus()
  return true
}

/**
 * A new paragraph beside this block — after it, or before it with a modifier.
 *
 * The caret lands in the new one, because the only reason to press this is to
 * write there. Adding a block and leaving the caret where it was is the shape
 * that makes people press it twice.
 */
export function addBlock(editor: Editor, where: 'above' | 'below'): boolean {
  const { state } = editor
  const here = blockAt(state)
  if (!here) return false
  const node = state.doc.child(here.index)
  const at = where === 'below' ? here.pos + node.nodeSize : here.pos
  const tr = state.tr.insert(at, state.schema.nodes['paragraph']!.create())
  tr.setSelection(TextSelection.near(tr.doc.resolve(at + 1)))
  tr.scrollIntoView()
  editor.view.dispatch(tr)
  editor.view.focus()
  return true
}

/** Select the whole block, which is what a comment on one is about. */
export function selectBlock(editor: Editor, pos: number): boolean {
  const { state } = editor
  const node = state.doc.nodeAt(pos)
  if (!node) return false
  const tr = state.tr.setSelection(NodeSelection.create(state.doc, pos))
  editor.view.dispatch(tr)
  editor.view.focus()
  return true
}
